/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable react/jsx-props-no-spreading */
import React, { createElement, memo, useRef } from 'react';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import directive from 'remark-directive';
import { visit } from 'unist-util-visit';
import { Node, Data } from 'unist';
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';
import { BlockMath, InlineMath } from 'react-katex';
import rehypeRaw from 'rehype-raw';

import CodeSnippet from '../codeBox/CodeSnippet';
import { isNullOrUndefined, isEmpty } from '../../../../utils/type';
import HighlightSection from './highlightSection';
import cn from 'classnames';
import { Logger } from '@utils/logger';

const extraPropertiesRegex = /([^{]+)*\{([^}]+)\}/;
const logger = new Logger('react-markdown-custom');

function reactMarkdownRemarkDirective() {
  return (tree: Node<Data>) => {
    visit(tree, (node: any) => {
      // eslint-disable-next-line no-param-reassign
      node.data = {
        hType: node.type,
        hName: node.name,
        hProperties: node.attributes,
        hPosition: node.position,
        ...node.data,
      };
      return node;
    });
  };
}

const rmPlugins = [
  remarkGfm,
  directive,
  reactMarkdownRemarkDirective,
  remarkMath,
];

const ReactMarkdownCustom = ({
  markdown,
  slug,
  showCopyButton,
  components,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  className,
  markdownClassName,
  widgetComp,
  headingCount,
  runBtnClass = '',
  runBtnFunction,
  commentsPosition,
}: {
  markdown: string;
  slug?: string;
  showCopyButton?: boolean;
  components?: Object;
  className?: string;
  markdownClassName?: string;
  widgetComp?: Function;
  headingCount?: Number;
  runBtnClass?: string;
  runBtnFunction?: Function;
  commentsPosition?: Object[];
}) => {
  const getComments = (line_number: any) => {
    if (line_number) {
      return commentsPosition?.filter((comment) => comment.attributes.line_number === line_number);
    } else return ([])
  };

  const generateSpan = ({
    data,
    start,
    length,
    isComment,
    extraClassName,
    comment_id,
    node
  }: {
    data:
      | string
      | number
      | boolean
      | React.ReactElement<
          any /* eslint-disable react/jsx-props-no-spreading */,
          string | React.JSXElementConstructor<any>
        >
      | React.ReactFragment
      | React.ReactPortal
      | null
      | undefined;
    start: number;
    length: number;
    isComment: boolean;
    extraClassName?: string;
    comment_id?: number;
    node?: any;
  }) => {
    return createElement(
      'span',
      {
        className: classNames(
          'scrollable-line',
          { 'has-comment': isComment },
          { [`${extraClassName}`]: extraClassName }
        ),
        [`data-size`]: length,
        [`data-position`]: start,
        [`data-comment-id`]: isComment ? comment_id : null,
        [`data-startline`]: node.position?.start?.line,
        [`data-endline`]: node.position?.end?.line,
      },
      data
    )
  }

  const createParentEl = (node, extraClassName: string = '') => {
    const { children } = node;

    if (node.tagName === 'code') {
      return getCode(node, node.properties?.className, [children[0]?.value], {})
    }

    if (node.tagName === 'img') {
      return (
        <img
          className="image scrollable-line"
          data-startline={node.position?.start.line}
          data-endline={node.position?.end.line}
          data-position={node.position?.start.offset}
          data-size={node.position?.end?.offset - node.position?.start.offset}
          src={`${node.properties?.src}`}
          alt={`${node.properties?.alt}`}
        />
      );
    }
    if (node.type === 'element') {
      if (node?.properties?.className?.includes('math-inline') && node?.children?.length) {
        return (
          <span
            className='scrollable-line'
            data-startline={node.position?.start.line}
            data-endline={node.position?.end.line}
            data-position={node.position?.start.offset}
            data-size={node.position?.end?.offset - node.position?.start.offset}
          >
            <InlineMath>{String(children[0]?.value)}</InlineMath>
          </span>
        );
      }
      const childrenArray = []
      children?.forEach((child) => {
        const insideEl = createParentEl(child, extraClassName);
        if (insideEl) {
          childrenArray.push(insideEl);
        }
      });
      const el =  createElement(
        node.tagName,
        { className: classNames('scrollable-line'),
          [`data-size`]: 0,
          [`data-position`]: node.position?.start.offset,
          [`data-startline`]: node.position?.start.line,
          [`data-endline`]: node.position?.end.line,
          ...(node.tagName === 'ol' && node?.properties?.start) ? {[`start`]: node?.properties?.start} : {},
        },
        ...childrenArray
      );
      return el;
    } else {
      if (node.value != '\n') {
        const comments = getComments(node.position?.start?.line) || [];
        let childEl;
        if (comments.length) {
          childEl = generateCommentedCommentsLayout(node, node.value, extraClassName)
        } else {
          try {
            if (node.position.end) {
              childEl = generateSpan({
                data: node.value, start: node.position.start.offset, length: node.position.end?.offset - node.position.start.offset, isComment: false, node, extraClassName
              })
            } else {
              // Case for 1st child of nested list parent (end is undefined)
              childEl = generateSpan({data: node.value, start: node.position.start.offset, length: node.value.split("\n")[0].length, isComment: false, node, extraClassName})
            }
          } catch (error: any) {
            console.log(error);
          }
        }
        return childEl;
      }
    }
  };

  const generateCommentedCommentsLayout = (node, children, extraClassName = '') => {
    const comments = getComments(node.position?.start?.line) || [];
    const parts = [];
    let st = Number(node.position?.start.offset);
    let parentStartIndex = Number(node.position?.start.offset);
    comments?.forEach(({ attributes: comment }) => {
      if (comment.anchor - st > 0) {
        // parts.push(children[0].slice(st, comment.anchor));
        parts.push(generateSpan({data: children.slice(st - parentStartIndex, comment.anchor - parentStartIndex),start: st, length: comment.anchor - st,isComment: false,extraClassName, node}));
      } 
      // parts.push(children[0].slice(comment.anchor, comment.anchor + comment.length));
      parts.push(generateSpan({data: children.slice(comment.anchor - parentStartIndex, comment.anchor + comment.length - parentStartIndex),start: comment.anchor,length: comment.length, isComment: true, extraClassName,comment_id: comment.comment_id, node}));
      st = comment.anchor + comment.length;
    });
    // console.log('children', children);
    if (children.length + parentStartIndex - st > 0) { // Pick the last commented section or the whole children in case of no comments
      // parts.push(children[0].slice(st, children[0].length))
      const span = generateSpan({data: children.slice(st - parentStartIndex, children.length),start: st,length: children.length + parentStartIndex - st,isComment: false, extraClassName, node})
      // console.log(span)
      parts.push(span)
    }
    return parts;
  }

  // TODO: Can deprecate over time
  const generateCommentsLayout = (node, children, extraClassName = '') => {
    const parts = [];

    children?.forEach((child) => {
      if (typeof child === "string") {
        parts.push(...generateCommentedCommentsLayout(node, child, extraClassName).map((part) => part))
      } else {
        const { node } = child.props;
        if (!node || (Array.isArray(node.children) && node.children.length === 0)) {
          return;
        }
        let innerEl;
        // Different Tag ('strong', 'code')
        if (!['ul', 'ol'].includes(node.tagName)) {
          innerEl = generateSpan({
            data: node.children[0].value,
            start: Number(node.children[0].position.start.offset),
            length: Number(node.children[0].position.end.offset) - Number(node.children[0].position.start.offset),
            isComment: false,
            extraClassName, node}
          );
        } else {
          // Case of nested list
          const innerSpan = generateSpan({
            data: node.children[1].children[0].value,
            start: Number(node.children[1].children[0].position.start.offset),
            length: Number(node.children[1].children[0].position.end.offset) - Number(node.children[1].children[0].position.start.offset),
            isComment: false,
            extraClassName, node}
          );
          innerEl = createElement(
            'li',
            { className: classNames({ [`${extraClassName}`]: extraClassName}),
              [`data-size`]: 0,
              [`data-position`]: node.position?.start.offset,
              [`data-startline`]: node.position?.start.line,
              [`data-endline`]: node.position?.end.line,
            },
            innerSpan
          );
        }

        const el = createElement(
          node.tagName,
          { className: classNames({ [`${extraClassName}`]: extraClassName}, 'scrollable-line'),
            [`data-size`]: 0,
            [`data-position`]: node.position?.start.offset,
            [`data-startline`]: node.position?.start.line,
            [`data-endline`]: node.position?.end.line,
          },
          innerEl
        );
        parts.push(el);
      }
    });
    return parts;
  }

  const getCode = (node, className, children, props) => {
    const comments = getComments(node.position?.start?.line);
    const match = /language-(\w+)/.exec(className || '');
    const url = /::(.+)/.exec(className || '');
    if (isNullOrUndefined(match)) {
      const extraClassName = 'highlight--red';
      return generateCommentsLayout(node, children, extraClassName).map(
        (part: any) => part
      );
    }
    if (comments?.length) {
      return (
        <CodeSnippet
          code={String(children)}
          language={(match && match[1]) || 'text'}
          url={url ? url[1] : ''}
          runBtnClass={runBtnClass}
          runBtnFunction={runBtnFunction}
          node={node}
          comments={comments}
          {...props}
        />
      );
    }
    return (
      <CodeSnippet
        code={String(children)}
        language={(match && match[1]) || 'text'}
        url={url ? url[1] : ''}
        runBtnClass={runBtnClass}
        runBtnFunction={runBtnFunction}
        node={node}
        {...props}
      />
    );
  };

  return (
    <>
      <ReactMarkdown
        linkTarget="_blank"
        className={cn('markdown-body', {
          [`${markdownClassName}`]: markdownClassName,
        })}
        remarkPlugins={rmPlugins}
        rehypePlugins={[rehypeRaw]}
        components={{
          a: ({
            node, children, className, ...props
          }) => {
            let title = '';
            let attributes = {};
            if (props.title) {
              try {
                const matches = extraPropertiesRegex.exec(props.title);
                if (matches) {
                  title = matches[1];
                  if (matches[2]) {
                    try {
                      const pairs = matches[2].split(',');
                      attributes = pairs.reduce((acc: any, pair: string) => {
                        const [key, value] = pair.split('=');
                        acc[key] = value;
                        return acc;
                      }, {});
                    } catch (_err) {
                      // Error in extra attributes
                    }
                  }
                }
              } catch (_error) {
                // error
              }
            }
            return (
              <a
                data-startline={node.position?.start.line}
                data-endline={node.position?.end.line}
                data-position={node.position?.start.offset}
                data-size={0}
                className={classNames(className, 'scrollable-line')}
                {...props}
                title={title}
                {...attributes}
              >
                {generateCommentsLayout(node, children, className).map((part) => part)}
              </a>
            );
          },
          img: ({ node }) => {
            if (node.tagName === 'img') {
              return (
                <img
                  className="image scrollable-line"
                  data-startline={node.position?.start.line}
                  data-endline={node.position?.end.line}
                  data-position={node.position?.start.offset}
                  data-size={node.position?.end?.offset - node.position?.start.offset}
                  src={`${node.properties?.src}`}
                  alt={`${node.properties?.alt}`}
                />
              );
            }
            return null;
          },
          code: ({
            node, inline, className, children, ...props
          }) => getCode(node, className, children, props),
          span: ({
            node, children, className, ...props
          }) => {
            if (className?.includes('math-inline')) {
              return (
                <span
                  className='scrollable-line'
                  data-startline={node.position?.start.line}
                  data-endline={node.position?.end.line}
                  data-position={node.position?.start.offset}
                  data-size={node.position?.end?.offset - node.position?.start.offset}
                >
                  <InlineMath>{String(children[0])}</InlineMath>
                </span>
              );
            }
            return (
              generateCommentsLayout(node, children, className).map((part: any) => part)
            );
          },
          div: ({
            node, children, className, ...props
          }) => {
            if (className?.includes('math-display')) {
              return <div data-startline={node.position?.start.line}
              data-endline={node.position?.end.line}
              data-position={node.position?.start.offset}
              data-size={node.position?.end?.offset - node.position?.start.offset}
              className={classNames(className, 'scrollable-line')}
              {...props}><BlockMath>{String(children[0])}</BlockMath></div>;
            }
            return (
              <div data-startline={node.position?.start.line}
              data-endline={node.position?.end.line}
              data-position={node.position?.start.offset}
              data-size={0}
              className={classNames(className, 'scrollable-line')} {...props}>
                {generateCommentsLayout(node, children, className).map((part: any) => part)}
              </div>
            );
          },
          section: ({
            node, children, className, ...props
          }) => {
            if (className === 'highlight') {
              return <HighlightSection node={node}>{children}</HighlightSection>;
            }
            return (
              <section
              // data-startline={node.position?.start.line}
              data-endline={node.position?.end.line}
              data-position={node.position?.start.offset}
              data-size={0}
              className={classNames(className)} {...props}>
                {children}
                {/* {generateCommentsLayout(node, children, className).map((part: any) => part)} */}
              </section>
            );
          },
          h2: ({
            node, children, className, ...props
          }) => {
            return createParentEl(node, className);
            // return (
            //   <>
            //     <h2 data-startline={node.position?.start.line}
            //         data-endline={node.position?.end.line}
            //         data-position={node.position?.start.offset}
            //         data-size={0}
            //         className={className} {...props}>
            //       {generateCommentsLayout(node, children, className).map((part: any) => part)}
            //     </h2>
            //   </>
            // );
          },
          h3: ({
            node, children, className, ...props
          }) => {
            return createParentEl(node, className);
            // return (
            //   <>
            //     <h3 data-startline={node.position?.start.line}
            //         data-endline={node.position?.end.line}
            //         data-position={node.position?.start.offset}
            //         data-size={0}
            //         className={className} {...props}>
            //       {generateCommentsLayout(node, children, className).map((part: any) => part)}
            //     </h3>
            //   </>
            // );
          },
          table: ({
            node, children, className, ...props
          }) => (
            <div className='scrollable-line' data-startline={node.position?.start.line}
            data-endline={node.position?.end.line}
            data-position={node.position?.start.offset}
            data-size={node.position?.end?.offset - node.position?.start.offset}
            style={{ overflow: 'auto' }}>
              <table className={className} {...props}>
                {children}
              </table>
            </div>
          ),
          p: ({
            node, children, className, ...props
          }) => {
            if (node.children?.[0]?.tagName?.toLowerCase() === 'img') {
              const data = node.children[0];
              return (
                <img
                  className="image scrollable-line"
                  data-startline={data.position?.start.line}
                  data-endline={data.position?.end.line}
                  data-position={data.position?.start.offset}
                  data-size={data.position?.end?.offset - data.position?.start.offset}
                  src={`${data.properties?.src}`}
                  alt={`${data.properties?.alt}`}
                />
              );
            }
            return createParentEl(node, className);
              // <p
              //   data-startline={node.position?.start.line}
              //   data-endline={node.position?.end.line}
              //   data-position={node.position?.start.offset}
              //   data-size={0}
              //   className={className}
              //   {...props}
              // >
              //   {generateCommentsLayout(node, children).map((part) => part)}
              // </p>
          },
          ul: ({
            node, children, className, ...props
          }) => {
            return createParentEl(node, className);
            // return (
            //   <ul
            //     data-startline={node.position?.start.line}
            //     data-endline={node.position?.end.line}
            //     data-position={node.position?.start.offset}
            //     data-size={0}
            //     className={className}
            //     {...props}
            //   >
            //     {children}
            //   </ul>
            // );
          },
          ol: ({
            node, children, className, ...props
          }) => {
            return createParentEl(node, className);
            // return (
            //   <ol
            //     data-startline={node.position?.start.line}
            //     data-endline={node.position?.end.line}
            //     data-position={node.position?.start.offset}
            //     data-size={0}
            //     className={className}
            //     {...props}
            //   >
            //     {children}
            //   </ol>
            // );
          },
          li: ({
            node, children, className, ...props
          }) => {
            return createParentEl(node, className);
            // return (
            //   <li
            //     data-startline={node.position?.start.line}
            //     data-endline={node.position?.end.line}
            //     data-position={node.position?.start.offset}
            //     data-size={0}
            //     className={className}
            //     {...props}
            //   >
            //     {generateCommentsLayout(node, children).map((part) => part)}
            //   </li>
            // );
          },
          ...components,
        }}
      >
        {markdown}
      </ReactMarkdown>
    </>
  );
};

export default ReactMarkdownCustom
